// What each staff member may see and do in the admin. Plain data (no database code), shared by the
// API (requirePermission), the seed (built-in roles) and the admin app (what to show).
//
// A staff member's permissions are the union of their roles'. The owner role always has every
// permission, including ones added later. Built-in roles are kept in step with this file by the
// seed on every deploy; roles created in the admin are left as staff set them.

export const PERMISSIONS = {
  'overview.view': { area: 'Overview', label: 'See the overview and setup checklist' },
  'users.view': { area: 'Users', label: 'See users, balances and history' },
  'users.manage': { area: 'Users', label: 'Restrict or block users, add and close flags' },
  'users.adjust': { area: 'Users', label: 'Add or remove coins by hand' },
  'users.kyc': { area: 'Users', label: 'See identity check details (personal data)' },
  'holds.decide': { area: 'Users', label: 'Release or take back held coins' },
  'withdrawals.view': { area: 'Cash-outs', label: 'See cash-outs' },
  'withdrawals.decide': { area: 'Cash-outs', label: 'Approve, hold or reject cash-outs' },
  'giftcards.view': { area: 'Gift cards', label: 'See the gift card catalogue' },
  'giftcards.manage': { area: 'Gift cards', label: 'Sync, price and hide gift cards' },
  'decisions.view': { area: 'Decisions', label: 'See Claude’s cash-out and fraud reviews' },
  'providers.view': { area: 'Networks', label: 'See networks and their setup' },
  'providers.manage': { area: 'Networks', label: 'Change networks, send test postbacks' },
  'providers.secrets': { area: 'Networks', label: 'Reveal network keys' },
  'postbacks.view': { area: 'Networks', label: 'See the postback log' },
  'countries.manage': {
    area: 'Networks',
    label: 'Change country rules: network order, hidden networks, closing a country',
  },
  'reversals.apply': { area: 'Networks', label: 'Take back rewards a network reversed' },
  'support.view': { area: 'Support', label: 'Read support conversations' },
  'support.reply': { area: 'Support', label: 'Reply to users' },
  'support.manage': { area: 'Support', label: 'Close, reopen and snooze conversations' },
  'faq.manage': { area: 'Support', label: 'Write and publish FAQ answers' },
  'play.view': { area: 'Play Store', label: 'See Play Store reviews and ratings' },
  'play.reply': { area: 'Play Store', label: 'Reply to Play Store reviews' },
  'legacy.view': { area: 'Old accounts', label: 'See old-account links' },
  'legacy.decide': { area: 'Old accounts', label: 'Approve or reject old-account balances' },
  'notifications.send': {
    area: 'Notifications',
    label: 'Send notifications and emails to users',
  },
  'settings.view': { area: 'Settings', label: 'See settings' },
  'settings.manage': { area: 'Settings', label: 'Change settings' },
  'audit.view': { area: 'Settings', label: 'See the audit log' },
  'staff.manage': { area: 'Team', label: 'Invite staff, change roles, remove staff' },
  'assistant.use': { area: 'Assistant', label: 'Use the admin assistant' },
} as const satisfies Record<string, { area: string; label: string }>;

export type Permission = keyof typeof PERMISSIONS;
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];
export const isPermission = (p: string): p is Permission => p in PERMISSIONS;

export const OWNER_ROLE = 'owner';

/**
 * Permissions that come close to everything: running the team, closing a country, the networks'
 * keys (fake postbacks), coins by hand, sending cash-outs and the live settings. Giving any of them (a role
 * given, an invite, a role made or changed) takes a fresh code from the giver's authenticator, and
 * the owners get an email (apps/api routes/admin/team.ts).
 */
export const POWERFUL_PERMISSIONS: readonly Permission[] = [
  'staff.manage',
  'countries.manage',
  'providers.secrets',
  'users.adjust',
  'withdrawals.decide',
  'settings.manage',
];

const VIEW = ALL_PERMISSIONS.filter((p) => p.endsWith('.view'));

/** Roles every install has. Their permissions follow this file; staff can add roles of their own. */
export const BUILT_IN_ROLES: {
  id: string;
  name: string;
  description: string;
  permissions: readonly Permission[];
}[] = [
  {
    id: OWNER_ROLE,
    name: 'Owner',
    description: 'Everything, including the team. Can’t be changed or removed.',
    permissions: ALL_PERMISSIONS,
  },
  {
    id: 'admin',
    name: 'Admin',
    description: 'Everything except managing the team.',
    permissions: ALL_PERMISSIONS.filter((p) => p !== 'staff.manage'),
  },
  {
    id: 'support',
    name: 'Support',
    description: 'Answers users: support, FAQ, Play Store reviews, and read-only accounts.',
    permissions: [
      'overview.view',
      'users.view',
      'withdrawals.view',
      'postbacks.view',
      'legacy.view',
      'support.view',
      'support.reply',
      'support.manage',
      'faq.manage',
      'play.view',
      'play.reply',
      'assistant.use',
    ],
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Cash-outs, reversals, gift cards and old-account balances.',
    permissions: [
      'overview.view',
      'users.view',
      'holds.decide',
      'withdrawals.view',
      'withdrawals.decide',
      'giftcards.view',
      'giftcards.manage',
      'decisions.view',
      'providers.view',
      'postbacks.view',
      'reversals.apply',
      'legacy.view',
      'legacy.decide',
      'assistant.use',
    ],
  },
  {
    id: 'viewer',
    name: 'Viewer',
    description: 'Can look at everything, change nothing.',
    permissions: VIEW,
  },
];
